/**
 * SPA Service for PrimeReact
 * Provides API integration with HAPI V1 for member and order management
 */

class SPAService {
    constructor(baseUrl = 'https://www.sportendurance.co.uk') {
        this.baseUrl = baseUrl;
        this.isAuthenticated = false;
        this.currentMember = null;
        
        // Configure fetch defaults for cross-domain requests with credentials
        this.defaultOptions = {
            credentials: 'include', // Include cookies for authentication
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        };
    }

    /**
     * Make authenticated API request
     * @private
     */
    async apiRequest(endpoint, options = {}) {
        const url = `${this.baseUrl}/api/spa/${endpoint}`;
        
        const config = {
            ...this.defaultOptions,
            ...options,
            headers: {
                ...this.defaultOptions.headers,
                ...(options.headers || {})
            }
        };

        try {
            const response = await fetch(url, config);
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            // Handle API error responses
            if (data.success === false) {
                throw new Error(data.error || 'API request failed');
            }

            return data;
        } catch (error) {
            console.error(`API Error [${endpoint}]:`, error);
            throw error;
        }
    }

    // #region Member Management

    /**
     * Register a new member
     * @param {Object} memberData - Member registration data
     * @param {string} memberData.email - Member email
     * @param {string} memberData.password - Member password
     * @param {string} memberData.firstName - Member first name
     * @param {string} memberData.lastName - Member last name
     * @param {string} [memberData.phoneNumber] - Member phone number
     * @param {string} [memberData.mobileNumber] - Member mobile number
     * @param {string} [memberData.memberTypeAlias] - Member type alias (default: "Member")
     * @param {Object} [memberData.address] - Member address
     * @returns {Promise<Object>} Registration result
     */
    async register(memberData) {
        const payload = {
            email: memberData.email,
            password: memberData.password,
            firstName: memberData.firstName,
            lastName: memberData.lastName,
            phoneNumber: memberData.phoneNumber || null,
            mobileNumber: memberData.mobileNumber || null,
            memberTypeAlias: memberData.memberTypeAlias || 'Member',
            address: memberData.address || null
        };

        const result = await this.apiRequest('register', {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        return result;
    }

    /**
     * Login member
     * @param {string} email - Member email
     * @param {string} password - Member password
     * @param {boolean} [rememberMe=false] - Remember login
     * @returns {Promise<Object>} Login result with member info
     */
    async login(email, password, rememberMe = false) {
        const payload = {
            email,
            password,
            rememberMe
        };

        const result = await this.apiRequest('login', {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        if (result.success) {
            this.isAuthenticated = true;
            this.currentMember = {
                id: result.memberId,
                name: result.memberName,
                email: result.email
            };
        }

        return result;
    }

    /**
     * Sign out current member
     * @returns {Promise<Object>} Sign out result
     */
    async signOut() {
        const result = await this.apiRequest('signout', {
            method: 'POST'
        });

        if (result.success) {
            this.isAuthenticated = false;
            this.currentMember = null;
        }

        return result;
    }

    /**
     * Get current member information
     * @returns {Promise<Object>} Current member data
     */
    async getCurrentMember() {
        const result = await this.apiRequest('member', {
            method: 'GET'
        });

        if (result.success && result.isAuthenticated) {
            this.isAuthenticated = true;
            this.currentMember = result.member;
        } else {
            this.isAuthenticated = false;
            this.currentMember = null;
        }

        return result;
    }

    // #endregion

    // #region Order Management

    /**
     * Create a new order
     * @param {Object} orderData - Order creation data
     * @param {string} [orderData.currencyCode='GBP'] - Currency code
     * @param {string} [orderData.orderNotes] - Order notes
     * @param {Object} orderData.customer - Customer information
     * @param {string} orderData.customer.email - Customer email
     * @param {string} orderData.customer.firstName - Customer first name
     * @param {string} orderData.customer.lastName - Customer last name
     * @param {string} [orderData.customer.phoneNumber] - Customer phone
     * @param {Object} [orderData.customer.billingAddress] - Billing address
     * @param {Object} [orderData.customer.deliveryAddress] - Delivery address
     * @returns {Promise<Object>} Order creation result
     */
    async createOrder(orderData) {
        const payload = {
            currencyCode: orderData.currencyCode || 'GBP',
            orderNotes: orderData.orderNotes || null,
            customer: {
                email: orderData.customer.email,
                firstName: orderData.customer.firstName,
                lastName: orderData.customer.lastName,
                phoneNumber: orderData.customer.phoneNumber || null,
                billingAddress: orderData.customer.billingAddress || null,
                deliveryAddress: orderData.customer.deliveryAddress || null
            }
        };

        const result = await this.apiRequest('order', {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        return result;
    }

    /**
     * Add a product line to an order
     * @param {string} orderId - Order ID (GUID)
     * @param {string} productReference - Product GUID reference
     * @param {number} [quantity=1] - Product quantity
     * @param {Object} [properties={}] - Additional product properties
     * @returns {Promise<Object>} Add order line result
     */
    async addOrderLine(orderId, productReference, quantity = 1, properties = {}) {
        const payload = {
            orderId,
            productReference,
            quantity,
            properties
        };

        const result = await this.apiRequest('orderline', {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        return result;
    }



    // #endregion

    // #region Horse Methods
    
    /**
     * Get list of horses registered to the current member
     * @returns {Promise<Object>} Horse list result
     * @returns {Promise<Object>} result.success - Whether the request was successful
     * @returns {Promise<Array>} result.horses - Array of horse registrations
     * @returns {Promise<Object>} result.horses[].id - Horse registration ID
     * @returns {Promise<string>} result.horses[].horseName - Horse name
     * @returns {Promise<string>} result.horses[].memberId - Owner member ID
     * @returns {Promise<string>} result.horses[].dateOfBirth - Date of birth (ISO string)
     * @returns {Promise<string>} result.horses[].breed - Horse breed
     * @returns {Promise<string>} result.horses[].gender - Horse gender
     * @returns {Promise<string>} result.horses[].height - Horse height
     * @returns {Promise<string>} result.horses[].colour - Horse colour
     * @returns {Promise<boolean>} result.horses[].breedSocietyRegistered - Whether registered with breed society
     * @returns {Promise<string>} result.horses[].breedSocietyDetails - Breed society registration details
     */
    async getHorseList() {
        const result = await this.apiRequest('horse/list', {
            method: 'GET'
        });
        return result;
    }

    /**
     * Get list of all horses (admin only)
     * @returns {Promise<Object>} Horse list result
     * @returns {Promise<Object>} result.success - Whether the request was successful
     * @returns {Promise<Array>} result.horses - Array of all horse registrations
     */
    async getHorseListAll() {
        const result = await this.apiRequest('horse/list/all', {
            method: 'GET'
        });
        return result;
    }

    /**
     * Get list of rides (events) registered to the current member
     * @returns {Promise<Array>} Array of ride/event registrations
     * @returns {Promise<string>} rides[].eventId - Event GUID
     * @returns {Promise<string>} rides[].eventName - Event name
     * @returns {Promise<string>} rides[].eventUrl - Event page absolute URL
     * @returns {Promise<Object>} rides[].date - Event date object with singleDate/multipleDates
     * @returns {Promise<string>} rides[].location - Event location
     * @returns {Promise<Object>} rides[].rideResult - Ride result object
     * @returns {Promise<number>} rides[].rideResult.id - Rider result ID
     * @returns {Promise<Object>} rides[].rideResult.horse - Horse registration object (HorseRegistration)
     * @returns {Promise<number>} rides[].rideResult.bibNo - Rider bib number
     * @returns {Promise<string>} rides[].orderLineId - Order line ID (GUID)
     */
    async getMyRides() {
        const result = await this.apiRequest('my-rides', {
            method: 'GET'
        });
        return result;
    }

    /**
     * Cancel a ride registration by rider result ID
     * @param {number} riderResultId - Rider result ID to cancel
     * @returns {Promise<Object>} Cancellation result
     * @returns {Promise<boolean>} result.success - Whether the cancellation was successful
     * @returns {Promise<string>} result.error - Error message if unsuccessful
     */
    async cancelOrder(riderResultId) {
        try {
            const result = await this.apiRequest(`cancel-order?riderResultId=${riderResultId}`, {
                method: 'GET'
            });
            return { success: true };
        } catch (error) {
            console.error('Cancel order error:', error);
            return {
                success: false,
                error: error.message || 'Failed to cancel order'
            };
        }
    }

    /**
     * Assign a horse to a ride result
     * @param {Object} assignData - Horse assignment data
     * @param {number} assignData.riderResultId - Rider result ID (0 for new assignment)
     * @param {number} assignData.horseId - Horse ID to assign
     * @param {string} assignData.eventId - Event GUID
     * @param {string} assignData.orderLineId - Order line GUID
     * @returns {Promise<Object>} Assignment result
     * @returns {Promise<boolean>} result.success - Whether the assignment was successful
     * @returns {Promise<Object>} result.result - Rider result object
     * @returns {Promise<string>} result.error - Error message if unsuccessful
     */
    async assignHorseToResult(assignData) {
        try {
            const payload = {
                riderResultId: assignData.riderResultId || 0,
                horseId: assignData.horseId,
                eventId: assignData.eventId,
                orderLineId: assignData.orderLineId
            };

            const result = await this.apiRequest('result/assign-horse', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            return {
                success: result.success || true,
                result: result.result
            };
        } catch (error) {
            console.error('Assign horse error:', error);
            return {
                success: false,
                error: error.message || 'Failed to assign horse to ride'
            };
        }
    }

    /**
     * Update an existing horse registration
     * @param {Object} horseData - Horse update data
     * @param {number} horseData.id - Horse registration ID
     * @param {string} horseData.horseName - Horse name
     * @param {string} horseData.memberId - Owner member ID
     * @param {string} horseData.memberName - Owner member name
     * @param {string} horseData.dateOfBirth - Date of birth (ISO string)
     * @param {string} horseData.breed - Horse breed
     * @param {string} horseData.gender - Horse gender
     * @param {string} horseData.height - Horse height
     * @param {string} horseData.colour - Horse colour
     * @param {boolean} horseData.breedSocietyRegistered - Whether registered with breed society
     * @param {string} horseData.breedSocietyDetails - Breed society registration details
     * @returns {Promise<Object>} Update result
     * @returns {Promise<boolean>} result.success - Whether the update was successful
     * @returns {Promise<string>} result.error - Error message if unsuccessful
     */
    async updateHorse(horseData) {
        try {
            const payload = {
                id: horseData.id,
                horseName: horseData.horseName,
                memberId: horseData.memberId,
                memberName: horseData.memberName,
                dateOfBirth: horseData.dateOfBirth,
                breed: horseData.breed,
                gender: horseData.gender,
                height: horseData.height,
                colour: horseData.colour,
                breedSocietyRegistered: horseData.breedSocietyRegistered,
                breedSocietyDetails: horseData.breedSocietyDetails
            };

            const result = await this.apiRequest('horse/update', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            return result;
        } catch (error) {
            console.error('Update horse error:', error);
            return {
                success: false,
                error: error.message || 'Failed to update horse'
            };
        }
    }

    /**
     * Delete an existing horse registration
     * @param {number} horseId - Horse registration ID
     * @param {string} memberId - Owner member ID
     * @returns {Promise<Object>} Delete result
     * @returns {Promise<boolean>} result.success - Whether the delete was successful
     * @returns {Promise<string>} result.error - Error message if unsuccessful
     */
    async deleteHorse(horseId, memberId) {
        try {
            const result = await this.apiRequest(`horse/delete?horseId=${horseId}&memberId=${encodeURIComponent(memberId)}`, {
                method: 'DELETE'
            });

            return result;
        } catch (error) {
            console.error('Delete horse error:', error);
            return {
                success: false,
                error: error.message || 'Failed to delete horse'
            };
        }
    }
    
    // #endregion

    // #region Helper Methods

    /**
     * Check if user is currently authenticated
     * @returns {boolean} Authentication status
     */
    isUserAuthenticated() {
        return this.isAuthenticated;
    }

    /**
     * Get current member info (cached)
     * @returns {Object|null} Current member data
     */
    getCurrentMemberInfo() {
        return this.currentMember;
    }



    /**
     * Initialize service by checking authentication status
     * @returns {Promise<boolean>} Authentication status
     */
    async initialize() {
        try {
            const memberInfo = await this.getCurrentMember();
            return memberInfo.success && memberInfo.isAuthenticated;
        } catch (error) {
            console.warn('Failed to initialize SPA service:', error);
            return false;
        }
    }

    // #endregion

    // #region Payment Processing (Opayo Integration)

    /**
     * Process card payment via Opayo
     * @param {PaymentRequest} paymentRequest - Payment details
     * @returns {Promise<PaymentResponse>} Payment result
     */
    async processPayment(paymentRequest) {
        try {
            const result = await this.apiRequest('order/payment/callback', {
                method: 'POST',
                body: JSON.stringify(paymentRequest)
            });

            return {
                success: true,
                transactionId: result.transactionId,
                paymentResult: result.paymentResult,
                redirectUrl: result.redirectUrl
            };
        } catch (error) {
            console.error('Process payment error:', error);
            return {
                success: false,
                error: error.message || 'Payment processing failed'
            };
        }
    }

    /**
     * Handle 3D Secure authentication callback
     * @param {ThreeDSecureRequest} threeDSRequest - 3DS authentication data
     * @returns {Promise<PaymentResponse>} Final payment result
     */
    async handle3DSecure(threeDSRequest) {
        try {
            const result = await this.apiRequest('order/payment/3dsecure', {
                method: 'POST',
                body: JSON.stringify(threeDSRequest)
            });

            return {
                success: true,
                transactionId: result.transactionId,
                paymentResult: result.paymentResult,
                redirectUrl: result.redirectUrl
            };
        } catch (error) {
            console.error('3D Secure handling error:', error);
            return {
                success: false,
                error: error.message || '3D Secure authentication failed'
            };
        }
    }

    /**
     * Finalize order (optional - orders are auto-finalized on successful payment)
     * @param {string} orderId - Order GUID
     * @returns {Promise<Object>} Finalization result
     */
    async finalizeOrder(orderId) {
        try {
            const result = await this.apiRequest('order/finalize', {
                method: 'POST',
                body: JSON.stringify({ orderId })
            });

            return {
                success: true,
                message: result.message
            };
        } catch (error) {
            console.error('Finalize order error:', error);
            return {
                success: false,
                error: error.message || 'Failed to finalize order'
            };
        }
    }

    /**
     * Check 3D Secure authentication status
     * @param {string} orderId - Order GUID
     * @param {string} transactionId - Transaction/VendorTxCode
     * @returns {Promise<Object>} 3DS status { completed, success, status, error }
     */
    async check3DSecureStatus(orderId, transactionId) {
        try {
            const result = await this.apiRequest(`order/${orderId}/payment/${transactionId}/3ds-status`, {
                method: 'GET'
            });

            return {
                completed: result.completed || false,
                success: result.success || false,
                status: result.status || 'UNKNOWN',
                error: result.error || null
            };
        } catch (error) {
            console.error('3DS status check error:', error);
            return {
                completed: false,
                success: false,
                status: 'ERROR',
                error: error.message || 'Failed to check 3DS status'
            };
        }
    }

    // #endregion

    // #region Admin - Ride Management

    /**
     * Get all rides for all members (admin only)
     * @returns {Promise<Array>} Array of all rides with booking and arrival statistics
     */
    async getAllRides() {
        try {
            const result = await this.apiRequest('all-rides', {
                method: 'GET'
            });

            return result;
        } catch (error) {
            console.error('Get all rides error:', error);
            throw error;
        }
    }

    /**
     * Update rider result
     * @param {Object} updateData - Result update data
     * @param {number} updateData.riderResultId - Rider result ID
     * @param {boolean} updateData.arrived - Arrived status
     * @param {string} updateData.in - Time in
     * @param {string} updateData.out - Time out
     * @param {string} updateData.mileage - Mileage
     * @returns {Promise<Object>} Update result with updated rider result
     */
    async updateRiderResult(updateData) {
        try {
            const result = await this.apiRequest('result/update-result', {
                method: 'POST',
                body: JSON.stringify({
                    riderResultId: updateData.riderResultId,
                    bibNo: updateData.bibNo,
                    arrived: updateData.arrived,
                    in: updateData.in,
                    out: updateData.out,
                    mileage: updateData.mileage
                })
            });

            return result;
        } catch (error) {
            console.error('Update rider result error:', error);
            throw error;
        }
    }

    /**
     * Finalize ride results
     * @param {number} resultId - RideResults ID to finalize
     * @returns {Promise<Object>} Finalize result
     */
    async finalizeResults(resultId) {
        try {
            const result = await this.apiRequest('result/finalize-results', {
                method: 'POST',
                body: JSON.stringify({
                    resultId: resultId
                })
            });

            return result;
        } catch (error) {
            console.error('Finalize results error:', error);
            throw error;
        }
    }

    /**
     * Unlock ride results (set isFinalized to false)
     * @param {number} resultId - RideResults ID to unlock
     * @returns {Promise<Object>} Unlock result
     */
    async unlockResults(resultId) {
        try {
            const result = await this.apiRequest('result/unlock-results', {
                method: 'POST',
                body: JSON.stringify({
                    resultId: resultId
                })
            });

            return result;
        } catch (error) {
            console.error('Unlock results error:', error);
            throw error;
        }
    }

    /**
     * Get awards report data
     * @param {Date} [from] - Start date for filtering
     * @param {Date} [to] - End date for filtering
     * @returns {Promise<Array>} Array of rider/horse award data
     */
    async getAwardsReport(from = null, to = null) {
        try {
            const params = new URLSearchParams();
            if (from) {
                params.append('from', from.toISOString());
            }
            if (to) {
                params.append('to', to.toISOString());
            }
            
            const queryString = params.toString();
            const endpoint = `awards-report${queryString ? '?' + queryString : ''}`;
            
            const result = await this.apiRequest(endpoint, {
                method: 'GET'
            });

            return result;
        } catch (error) {
            console.error('Get awards report error:', error);
            throw error;
        }
    }

    /**
     * Download ride results as CSV
     * @param {number} rideResultId - Ride result ID
     * @param {string} [fileName] - Optional custom filename for download
     * @returns {Promise<void>} Downloads the CSV file
     */
    async downloadRideCSV(rideResultId, fileName = null) {
        try {
            const url = `${this.baseUrl}/api/spa/ride-csv?rideResultId=${rideResultId}`;
            
            const response = await fetch(url, {
                ...this.defaultOptions,
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            // Get the blob from response
            const blob = await response.blob();
            
            // Create a download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Set filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            let downloadFileName = fileName || 'ride-results.csv';
            
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch && fileNameMatch[1]) {
                    downloadFileName = fileNameMatch[1];
                }
            }
            
            link.download = downloadFileName;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Download ride CSV error:', error);
            throw error;
        }
    }

    /**
     * Cancel all orders associated with a specific event (admin only)
     * @param {string} eventId - Event GUID to cancel all orders for
     * @returns {Promise<Object>} Cancellation result with details for each order
     * @returns {Promise<boolean>} result.success - Whether the operation was successful
     * @returns {Promise<string>} result.eventId - Event GUID
     * @returns {Promise<number>} result.totalOrders - Total number of orders processed
     * @returns {Promise<Array>} result.results - Array of individual order cancellation results
     */
    async cancelEvent(eventId) {
        try {
            const result = await this.apiRequest('cancel-event', {
                method: 'POST',
                body: JSON.stringify({ eventId })
            });

            return result;
        } catch (error) {
            console.error('Cancel event error:', error);
            throw error;
        }
    }

    // #endregion
}

// Create and export singleton instance
export const spaService = new SPAService();

// Export class for custom instances
export { SPAService };

// Export types/interfaces for TypeScript projects
export const SPAServiceTypes = {
    RegisterMemberData: {
        email: 'string',
        password: 'string',
        firstName: 'string', 
        lastName: 'string',
        phoneNumber: 'string?',
        mobileNumber: 'string?',
        memberTypeAlias: 'string?',
        address: {
            line1: 'string?',
            line2: 'string?', 
            city: 'string?',
            county: 'string?',
            postcode: 'string?',
            country: 'string?'
        }
    },
    OrderData: {
        currencyCode: 'string?',
        orderNotes: 'string?',
        customer: {
            email: 'string',
            firstName: 'string',
            lastName: 'string',
            phoneNumber: 'string?',
            billingAddress: 'object?',
            deliveryAddress: 'object?'
        }
    },
    OrderLine: {
        productReference: 'string', // GUID
        quantity: 'number',
        properties: 'object?'
    }
};